/** Host command types stored in the Offline-mode IndexedDB log. */
export const OFFLINE_COMMAND_TYPES = {
  SEAT: 'seat',
  SCORE: 'score',
  JOIN_WAITLIST: 'join_waitlist',
  LEAVE_WAITLIST: 'leave_waitlist',
  REORDER_WAITLIST: 'reorder_waitlist',
  ADD_PLAYER: 'add_player',
  CREATE_GUEST: 'create_guest',
  PLAYER_STATUS: 'player_status',
  REMOVE_PLAYER: 'remove_player',
  PARTNERSHIP_ADD: 'partnership_add',
  PARTNERSHIP_REMOVE: 'partnership_remove',
  PREFERENCE: 'preference',
  LATE_PENALTY: 'late_penalty',
  SCHEDULED_GAME: 'scheduled_game',
  COURT_COUNT: 'court_count',
};

/** Flush / live-call budget. Live mode waits; it does not switch to the local engine. */
export const OFFLINE_NETWORK_TIMEOUT_MS = 4000;

export const OFFLINE_FLUSH_INTERVAL_MS = 8000;

/** Wait after the browser flips online before a flush burst (slow 4G often flips immediately). */
export const OFFLINE_FLUSH_ONLINE_DELAY_MS = 4000;

export const RETRYABLE_COMMAND_CODES = new Set([
  'EF_INVOKE_ERROR',
  'FINISH_MATCH_ERROR',
  'WAITLIST_JOIN_FAILED',
  'WAITLIST_LEAVE_FAILED',
  'WAITLIST_REORDER_FAILED',
  'COURT_ASSIGN_FAILED',
  'SET_NEXT_UP_FAILED',
]);

export const CONFLICT_COMMAND_CODES = new Set([
  'COURT_CONFLICT',
  'VERSION_MISMATCH',
  'STALE_TRANSITION',
  'SLOT_ALREADY_FILLED',
  'COURT_ALREADY_FILLED',
  'MATCH_NOT_ACTIVE',
  'PENDING_OTHER',
  'NOT_CLAIMED',
  'LOCK_CONTENTION',
  'TRANSITION_ALREADY_PREPARED',
]);

export function isRetryableCommandResult(result) {
  if (!result) return true;
  if (result.ok) return false;
  if (result.retryable === true) return true;
  if (CONFLICT_COMMAND_CODES.has(result.code)) return false;
  if (RETRYABLE_COMMAND_CODES.has(result.code)) return true;
  const message = String(result.message || '');
  return /network|timeout|fetch|Failed to send|Failed to fetch|offline|ERR_INTERNET/i.test(message);
}

export function isConflictCommandResult(result) {
  if (!result || result.ok) return false;
  return CONFLICT_COMMAND_CODES.has(result.code);
}

export function isLikelyNetworkError(error) {
  if (!error) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const message = String(error.message || error.details || error.hint || error);
  const code = String(error.code || '');
  const name = String(error.name || '');
  return (
    name === 'FunctionsFetchError' ||
    (name === 'TypeError' && /fetch/i.test(message)) ||
    code === 'ENOTFOUND' ||
    code === 'ECONNRESET' ||
    /network|timeout|fetch|Failed to send|Failed to fetch|offline|ERR_INTERNET/i.test(message)
  );
}

export function isBrowserOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Host turned Offline mode on: session flag or this phone's IndexedDB record. */
export function isHallOfflineMode(offlineQueue, session) {
  if (session?.offline_mode === true) return true;
  if (offlineQueue?.offlineMode === true) return true;
  if (offlineQueue?.record?.offlineMode === true) return true;
  return false;
}

/** Freeze this tab's courts while Offline mode is flushing or still has a log. */
export function shouldHoldLocalOfflineLayout(offlineQueue) {
  if (!offlineQueue) return false;
  if (offlineQueue.isFlushing) return true;
  if (typeof offlineQueue.hasUnsyncedCommands === 'function') {
    return offlineQueue.hasUnsyncedCommands();
  }
  return Boolean(offlineQueue.hasPending);
}

/** Host should run the local engine only in explicit Offline mode. */
export function shouldRunLocalHostPath(offlineQueue, session) {
  return isHallOfflineMode(offlineQueue, session);
}

/**
 * Do not paint server courts / upcoming on the hall phone.
 * complete-session-score may write a different server fill before seat overwrite.
 */
export function shouldHoldRemoteUpcoming(offlineQueue, session) {
  return isHallOfflineMode(offlineQueue, session);
}

export function canUseOfflineRosterFallback(offlineBridge) {
  return isHallOfflineMode(offlineBridge);
}

export function createOfflineCommandId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `offline-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
